import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useAccount } from '@/contexts/AccountContext'
import { useUserProfile } from '@/hooks/useSupabaseAuth'
import { Button } from '@/components/ui/button'
import { LogOut, FileText, Users, Settings, BarChart3, UsersIcon, FolderOpen, Link2, File, Shield, ClipboardCheck, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { WorkspaceSwitcher } from './WorkspaceSwitcher'
import { GetHelpDialog } from '@/components/help/GetHelpDialog'
import { resolveRoute } from '@/lib/workspace'
import { roleMeetsRequirement } from '@/lib/roles'
import type { UserRole } from '@/types/icrop'

interface LayoutProps {
  children: React.ReactNode
}

export function Layout({ children }: LayoutProps) {
  const { user, signOut } = useAuth()
  const { account } = useAccount()
  const { data: profile } = useUserProfile()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleSignOut = async () => {
    await signOut()
  }

  if (!user) {
    return <div className="min-h-screen bg-background">{children}</div>
  }

  // Workspace nav tabs only exist on workspace subdomains — on the admin shell
  // (app.icrop.net) those routes 404, so the admin console shows no tabs.
  const isAdminConsole = resolveRoute().kind === 'admin'
  const role = (profile?.role as UserRole | undefined) ?? null

  // Each item's `requiredRole` mirrors its route's ProtectedRoute gate so the
  // menu never surfaces a destination the route would reject ("hide what you
  // can't reach"). Items with no `requiredRole` are open to any authenticated
  // user (RLS scopes the data). `serviceType` hides FSVP-only tabs elsewhere.
  // Settings is intentionally admin-only in the nav: its /settings/notifications
  // child is reachable by URL for every role, but the menu entry stays gated.
  type NavItem = {
    href: string
    label: string
    icon: typeof BarChart3
    requiredRole?: UserRole
    serviceType?: 'fsvp' | 'document_control'
  }
  const allNavigationItems: NavItem[] = [
    { href: '/dashboard', label: 'Dashboard', icon: BarChart3 },
    { href: '/organizations', label: 'Organizations', icon: Users },
    { href: '/groups', label: 'Groups', icon: FolderOpen, serviceType: 'fsvp' },
    { href: '/documents', label: 'Documents', icon: File },
    { href: '/evaluation-forms', label: 'Evaluation Forms', icon: ClipboardCheck, requiredRole: 'super_admin', serviceType: 'fsvp' },
    { href: '/upload-links', label: 'Upload Links', icon: Link2, requiredRole: 'manager' },
    { href: '/users', label: 'Users', icon: UsersIcon, requiredRole: 'account_admin' },
    { href: '/audit-log', label: 'Audit Log', icon: Shield, requiredRole: 'account_admin' },
    { href: '/settings', label: 'Settings', icon: Settings, requiredRole: 'account_admin' },
  ]
  const navigationItems = isAdminConsole
    ? []
    : allNavigationItems.filter(
        (item) =>
          (!item.serviceType || account?.service_type === item.serviceType) &&
          (!item.requiredRole || roleMeetsRequirement(role, item.requiredRole)),
      )

  const isSuperAdmin = profile?.role === 'super_admin';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {account?.settings?.branding?.logo_url ? (
                <>
                  <img
                    src={account.settings.branding.logo_url}
                    alt={account.name}
                    className="h-10 w-auto shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground truncate">{account.name}</p>
                  </div>
                </>
              ) : (
                <>
                  <FileText className="h-7 w-7 text-primary shrink-0" />
                  <div className="min-w-0">
                    <h1 className="text-lg font-semibold truncate">iCrop</h1>
                    {account && (
                      <p className="text-xs text-muted-foreground truncate">{account.name}</p>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              {isSuperAdmin && (
                <div className="hidden sm:inline-flex">
                  <WorkspaceSwitcher />
                </div>
              )}

              <span className="text-sm text-muted-foreground hidden md:inline">
                {user.email}
              </span>
              <GetHelpDialog className="hidden sm:inline-flex" />
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignOut}
                className="hidden sm:inline-flex"
              >
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Sign Out</span>
              </Button>

              {/* Mobile menu toggle */}
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Desktop Navigation */}
      {navigationItems.length > 0 && (
      <nav className="border-b bg-muted/50 hidden lg:block">
        <div className="container mx-auto px-4">
          <div className="flex space-x-1 overflow-x-auto">
            {navigationItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.href

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "flex items-center space-x-2 px-3 py-3 text-sm font-medium transition-colors whitespace-nowrap",
                    isActive
                      ? "text-primary border-b-2 border-primary"
                      : "text-muted-foreground hover:text-primary"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </div>
        </div>
      </nav>
      )}

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <nav className="border-b bg-card lg:hidden">
          <div className="container mx-auto px-4 py-2">
            <div className="grid grid-cols-2 gap-1">
              {navigationItems.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.href

                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted"
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span>{item.label}</span>
                  </Link>
                )
              })}
            </div>
            <div className="border-t mt-2 pt-2 flex items-center justify-between">
              <span className="text-xs text-muted-foreground truncate">{user.email}</span>
              <div className="flex gap-1">
                {isSuperAdmin && (
                  <Button asChild variant="ghost" size="sm" onClick={() => setMobileMenuOpen(false)}>
                    <Link to="/admin">
                      <Settings className="h-4 w-4" />
                    </Link>
                  </Button>
                )}
                <GetHelpDialog variant="ghost" iconOnly />
                <Button variant="ghost" size="sm" onClick={handleSignOut}>
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 sm:py-8">
        {children}
      </main>
    </div>
  )
}